import Swiper from "swiper";
import { Navigation, Scrollbar } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import "./view.scss";

const SLIDER_SELECTOR = "[data-role='testimonial-slider']";
const CURSOR_SELECTOR = "[data-role='slider-cursor']";
const SCROLLBAR_SELECTOR = "[data-role='slider-scrollbar']";

function ensureCursorEl(block) {
  let cursorEl = block.querySelector(CURSOR_SELECTOR);
  if (cursorEl) return cursorEl;

  cursorEl = document.createElement("div");
  cursorEl.className = "c-testimonial__cursor";
  cursorEl.setAttribute("data-role", "slider-cursor");
  cursorEl.setAttribute("aria-hidden", "true");
  cursorEl.innerHTML = `
    <span class="c-testimonial__cursor-icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="24" fill="white"/>
        <path d="M26 26.8157L28.1347 29L33 23.998L28.1347 19L26 21.185L28.7478 23.998L26 26.8157Z" fill="#EC4F00"/>
        <path d="M22 26.8157L19.8653 29L15 23.998L19.8653 19L22 21.185L19.2522 23.998L22 26.8157Z" fill="#EC4F00"/>
      </svg>
    </span>
    <span class="c-testimonial__cursor-drag" aria-hidden="true"></span>
  `;
  block.appendChild(cursorEl);
  return cursorEl;
}

function initTestimonial(block) {
  const swiperEl = block.querySelector(SLIDER_SELECTOR);
  const cursorEl = ensureCursorEl(block);
  const scrollbarEl = block.querySelector(SCROLLBAR_SELECTOR);

  if (!swiperEl || !swiperEl.querySelector(".swiper-slide")) {
    return;
  }

  const prevBtn = block.querySelector(".c-testimonial__arrow--prev");
  const nextBtn = block.querySelector(".c-testimonial__arrow--next");

  const slideWrapper = swiperEl.querySelector(".swiper-wrapper");
  const originalSlides = Array.from(slideWrapper.querySelectorAll(".swiper-slide"));
  const originalSlideCount = originalSlides.length;

  const minRequiredSlides = 8;
  if (originalSlideCount > 1 && originalSlideCount < minRequiredSlides) {
    const clonesNeeded = Math.ceil(minRequiredSlides / originalSlideCount) - 1;
    for (let i = 0; i < clonesNeeded; i++) {
      originalSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add("c-testimonial__slide--cloned");
        clone.setAttribute("aria-hidden", "true");
        slideWrapper.appendChild(clone);
      });
    }
  }

  const loopEnabled = originalSlideCount >= 2;

  const swiper = new Swiper(swiperEl, {
    modules: [Navigation, Scrollbar],
    navigation: {
      nextEl: nextBtn,
      prevEl: prevBtn,
      disabledClass: "is-disabled",
    },
    slideToClickedSlide: true,
    speed: 1000,
    loop: loopEnabled,
    loopAdditionalSlides: Math.max(originalSlideCount, 4),
    slidesPerView: 1.15,
    spaceBetween: 20,
    effect: "slide",
    cssMode: false,
    grabCursor: false,
    allowTouchMove: true,
    scrollbar: {
      el: scrollbarEl || ".swiper-scrollbar",
      draggable: true,
      hide: false,
    },
    on: {
      setTransition(swiperInstance) {
        swiperInstance.wrapperEl.style.transitionTimingFunction =
          "cubic-bezier(0.34, 1.2, 0.64, 1)";
      },
    },
    breakpoints: {
      1025: {
        slidesPerView: 2,
        spaceBetween: 20,
        initialSlide: 1,
      },
    },
  });

  // Custom cursor: mimic "follow mouse" even while dragging
  if (cursorEl) {
    const CARD_SELECTOR = ".c-testimonial__card";
    let rafId = null;
    let overSlider = false;
    let lastPointerX = 0;
    let lastPointerY = 0;
    let hasPointerSample = false;

    const moveCursor = (e) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        cursorEl.style.left = `${e.clientX}px`;
        cursorEl.style.top = `${e.clientY}px`;
        rafId = null;
      });
    };

    const showCursor = () => {
      cursorEl.style.opacity = "1";
      cursorEl.style.visibility = "visible";
      cursorEl.setAttribute("aria-hidden", "false");
    };

    const hideCursor = () => {
      cursorEl.classList.remove("is-dragging");
      cursorEl.style.opacity = "0";
      cursorEl.style.visibility = "hidden";
      cursorEl.setAttribute("aria-hidden", "true");
    };

    const setDragging = (yes) => {
      cursorEl.classList.toggle("is-dragging", yes);
    };

    const pointerOverThisCard = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      const card = el?.closest?.(CARD_SELECTOR);
      return card && block.contains(card);
    };

    const syncCursorFromPointer = (clientX, clientY) => {
      moveCursor({ clientX, clientY });
      showCursor();
    };

    // Track when the pointer is over any card in the slider; position from enter event
    // so scroll-into-view without mousemove still shows the cursor in the right place.
    block.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      card.addEventListener("mouseenter", (e) => {
        overSlider = true;
        lastPointerX = e.clientX;
        lastPointerY = e.clientY;
        hasPointerSample = true;
        moveCursor(e);
        showCursor();
      });
      card.addEventListener("mouseleave", () => {
        overSlider = false;
        if (!cursorEl.classList.contains("is-dragging")) {
          hideCursor();
        }
      });
    });

    // When the page scrolls, content can move under the stationary pointer without
    // mouseenter/mousemove; sync using last known pointer position.
    // Coalesce to one check per animation frame: elementFromPoint forces layout,
    // so running it on every scroll event causes repeated forced reflows.
    let scrollSyncScheduled = false;
    const onScrollOrResize = () => {
      if (scrollSyncScheduled) return;
      scrollSyncScheduled = true;
      requestAnimationFrame(() => {
        scrollSyncScheduled = false;
        if (!hasPointerSample || cursorEl.classList.contains("is-dragging")) {
          return;
        }
        if (pointerOverThisCard(lastPointerX, lastPointerY)) {
          overSlider = true;
          syncCursorFromPointer(lastPointerX, lastPointerY);
        } else if (overSlider) {
          overSlider = false;
          hideCursor();
        }
      });
    };

    window.addEventListener("scroll", onScrollOrResize, { capture: true, passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    // Entering the swiper can occur without a subsequent mousemove; align cursor to the pointer.
    swiperEl.addEventListener("pointerenter", (e) => {
      if (!pointerOverThisCard(e.clientX, e.clientY)) return;
      overSlider = true;
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      hasPointerSample = true;
      moveCursor(e);
      showCursor();
    });

    // Swiper calls preventDefault() on pointermove while dragging, which suppresses
    // compatibility mousemove; pointermove still fires with correct coordinates.
    const onDragMove = (e) => {
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      hasPointerSample = true;
      if (!overSlider && !cursorEl.classList.contains("is-dragging")) return;
      moveCursor(e);
      showCursor();
    };

    document.addEventListener("pointermove", onDragMove, { passive: true });

    // Mouse drag start anywhere inside the swiper
    swiperEl.addEventListener("mousedown", (e) => {
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      hasPointerSample = true;
      setDragging(true);
      moveCursor(e);
      showCursor();
    });

    // Touch drag start
    swiper.on("touchStart", () => {
      setDragging(true);
    });

    // Touch drag end
    swiper.on("touchEnd", () => {
      setDragging(false);
      hideCursor();
    });

    // Release: use this event's coordinates — lastPointerX/Y can be stale if there was no pointermove
    // between hover and click (mouseup alone would wrongly hide the cursor).
    document.addEventListener("pointerup", (e) => {
      if (e.pointerType === "touch") return;
      setDragging(false);
      const cx = e.clientX;
      const cy = e.clientY;
      if (pointerOverThisCard(cx, cy)) {
        overSlider = true;
        lastPointerX = cx;
        lastPointerY = cy;
        hasPointerSample = true;
        syncCursorFromPointer(cx, cy);
      } else {
        overSlider = false;
        hideCursor();
      }
    });
  }
}

function initAllTestimonials() {
  document
    .querySelectorAll(".wp-block-blueprint-testimonial.c-testimonial:not(.c-testimonial--editor)")
    .forEach(initTestimonial);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAllTestimonials);
} else {
  initAllTestimonials();
}
